import { Input, Select } from "@frui.ts/bootstrap";
import { observer } from "mobx-react-lite";
import React from "react";
import { Col, Form } from "react-bootstrap";
import type BankAccountBlacklistDetailViewModel from "viewModels/finance/bankAccountBlacklists/bankAccountBlacklistDetailViewModel";
import type BankAccountBlacklistCreateViewModel from "viewModels/finance/bankAccountBlacklists/bankAccountBlacklistCreateViewModel";
import CzechAccountInput from "controls/finance/czechAccountInput";
import IbanInput from "controls/finance/ibanInput";
import SwiftInput from "controls/finance/swiftInput";
import BaseAccountNumberInput from "controls/finance/baseAccountNumberInput";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import { BankAccountFormatType } from "entities/bankAccountFormatType";
import BankAccount from "entities/bankAccount";

export interface BankAccountBlacklistFormProps {
  vm: BankAccountBlacklistDetailViewModel | BankAccountBlacklistCreateViewModel;
  children?: React.ReactNode;
}

const BankAccountBlacklistForm = observer((props: BankAccountBlacklistFormProps) => {
  const { vm, children } = props;
  const disabled = !vm.isEditable;
  const bankAccount = vm.item?.bankAccount || new BankAccount();
  let accountInputs;

  if (vm.item) {
    switch (bankAccount._formatType) {
      case BankAccountFormatType.CZECH:
        accountInputs = (
          <FormGroup as={Col} target={vm.item} property="bankAccount">
            <FormLabel required={true}>{vm.translateAttribute("bankAccount")}</FormLabel>
            <CzechAccountInput disabled={disabled} onInvalidValuePasted={vm.onInvalidAccountNumberPasted} />
          </FormGroup>
        );
        break;

      case BankAccountFormatType.IBAN:
        accountInputs = (
          <>
            <FormGroup as={Col} target={bankAccount} property="accountNumber">
              <FormLabel>{vm.translateGeneral("iban")}</FormLabel>
              <IbanInput disabled={disabled} onInvalidValuePasted={vm.onInvalidAccountNumberPasted} />
            </FormGroup>
            <FormGroup as={Col} md={5} target={bankAccount} property="bank">
              <FormLabel>{vm.translateGeneral("swift")}</FormLabel>
              <SwiftInput disabled={disabled} />
            </FormGroup>
          </>
        );
        break;

      case BankAccountFormatType.OTHER:
        accountInputs = (
          <>
            <FormGroup as={Col} target={bankAccount} property="accountNumber">
              <FormLabel>{vm.translateSubAttribute("bankAccount", "accountNumber")}</FormLabel>
              <BaseAccountNumberInput disabled={disabled} />
            </FormGroup>
            <FormGroup as={Col} md={5} target={bankAccount} property="bank">
              <FormLabel>{vm.translateSubAttribute("bankAccount", "bank")}</FormLabel>
              <Input disabled={disabled} />
            </FormGroup>
          </>
        );
        break;
    }
  }

  return (
    <>
      <Form.Row>
        <FormGroup as={Col} target={bankAccount} property="_formatType">
          <FormLabel required={true}>{vm.translateSubAttribute("bankAccount", "_formatType")}</FormLabel>
          <Select mode="key" items={vm.accountFormatTypes} disabled={disabled} />
        </FormGroup>
      </Form.Row>
      <Form.Row>{accountInputs}</Form.Row>
      <Form.Row>
        <FormGroup as={Col} target={vm.item} property="description">
          <FormLabel>{vm.translateAttribute("description")}</FormLabel>
          <Input disabled={disabled} />
        </FormGroup>
      </Form.Row>
      {children}
    </>
  );
});

export default BankAccountBlacklistForm;
